import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DistributionBars, TrackBar } from "@digital-twins/deck-ui/components";
import { DECK_CHART_PRIMARY_COLOR } from "@digital-twins/deck-ui/foundations/chart-colors";

import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { ReadStrengthTag } from "./ReadStrengthTag";
import { cutValueLine } from "./StorylineCutRows";
import { formatFixedDecimal } from "@/features/studies/report/page/components/tabs/formatAbsoluteScore";

import type { ReactNode } from "react";
import type { StorylineEvidenceUnit } from "@digital-twins/shared-types/reporting";
import type {
  FindingPlot,
  FindingPlotCut,
  FindingPlotCutGroup,
} from "@/features/studies/report/storylinePlots";

interface StorylineFigureProps {
  plot: FindingPlot;
  findingIndex: number;
  /** The finding's headline: the figure's name when its measure has none. */
  headline: string;
}

/** Bars that carry the read: the brand's first chart colour. */
const HIGHLIGHT_COLOR = DECK_CHART_PRIMARY_COLOR;
/** Bars outside the cited band or option: the muted foreground, faded. */
const CONTEXT_COLOR = "hsl(var(--muted-foreground) / 0.35)";

const CHIP_IDLE_CLASS =
  "max-w-[16rem] truncate rounded-full border border-border px-3 py-1 text-sm text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const CHIP_PRESSED_CLASS =
  "max-w-[16rem] truncate rounded-full border border-transparent px-3 py-1 text-sm bg-muted text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type AppTranslate = ReturnType<typeof useTranslation<"app">>["t"];

function formatShare(share: number): string {
  return `${formatFixedDecimal(share, 0)}%`;
}

/**
 * The bar width of a cut group: shares on 0..100, means on 0..scaleMax.
 * Null when the group has no value (counts only) or the scale is unknown.
 */
export function cutGroupWidthPct(
  cut: FindingPlotCut,
  group: FindingPlotCutGroup,
): number | null {
  if (group.value === null) {
    return null;
  }
  switch (cut.unit) {
    case "percent":
      return group.value;
    case "scale_points":
      return typeof cut.scaleMax === "number" && cut.scaleMax > 0
        ? (100 * group.value) / cut.scaleMax
        : null;
    case "index":
      return null;
    default: {
      const unhandled: never = cut.unit;
      return unhandled;
    }
  }
}

function unitWords(unit: StorylineEvidenceUnit | null, t: AppTranslate): string | undefined {
  switch (unit) {
    case "answers":
      return t("studyReport.storyline.figure.shareOf", {
        unit: t("studyReport.storyline.figure.unit.answers"),
      });
    case "people":
      return t("studyReport.storyline.figure.shareOf", {
        unit: t("studyReport.storyline.figure.unit.people"),
      });
    default:
      return undefined;
  }
}

/**
 * One finding's embedded plot, over deck-ui primitives: the only analyst
 * component that imports chart components (the plots-stage1 migration seam).
 * Cuts draw a `TrackBar` per group, with chips when there are two or more;
 * a rating draws its stored buckets, colouring the cited band; shares colour
 * the cited option; themes read as a distribution.
 */
export function StorylineFigure(props: StorylineFigureProps) {
  const { plot, findingIndex, headline } = props;

  const { t } = useTranslation("app");

  const testId = `storyline-figure-${findingIndex}`;

  let body: ReactNode;
  let ariaLabel: string;
  switch (plot.kind) {
    case "cut":
      return (
        <StorylineCutFigure
          cuts={plot.cuts}
          findingIndex={findingIndex}
          headline={headline}
        />
      );
    case "rating": {
      const hasHighlight = plot.buckets.some((bucket) => bucket.isCited);
      ariaLabel = t("studyReport.storyline.figure.distribution", { title: plot.label });
      body = (
        <BarRows
          testId={testId}
          rows={plot.buckets.map((bucket) => ({
            key: bucket.id,
            label: bucket.label,
            share: bucket.share,
            isCited: bucket.isCited,
          }))}
          hasHighlight={hasHighlight}
        />
      );
      break;
    }
    case "shares": {
      const hasHighlight = plot.rows.some((row) => row.isCited);
      ariaLabel = t("studyReport.storyline.figure.distribution", { title: plot.label });
      body = hasHighlight ? (
        <BarRows testId={testId} rows={plot.rows} hasHighlight />
      ) : (
        <DistributionBars
          rows={plot.rows.map((row) => ({ label: row.label, share: row.share }))}
          unit="%"
          unitLabel={unitWords(plot.unit, t)}
          labels={{ empty: t("studyReport.storyline.figure.empty") }}
        />
      );
      break;
    }
    case "themes":
      ariaLabel = t("studyReport.storyline.figure.distribution", { title: headline });
      body = (
        <DistributionBars
          rows={plot.rows.map((row) => ({ label: row.label, share: row.share }))}
          unit="%"
          unitLabel={unitWords(plot.unit, t)}
          labels={{ empty: t("studyReport.storyline.figure.empty") }}
        />
      );
      break;
    default: {
      const unhandled: never = plot;
      return unhandled;
    }
  }

  return (
    <FigureCard testId={testId} kind={plot.kind} ariaLabel={ariaLabel}>
      {body}
      <Text size="xs" color="muted" data-testid={`${testId}-base`}>
        {t("studyReport.storyline.base", { count: plot.base })}
      </Text>
    </FigureCard>
  );
}

interface FigureCardProps {
  testId: string;
  kind: FindingPlot["kind"];
  ariaLabel: string;
  children: ReactNode;
}

/** The figure card: full column width, content-driven height. */
function FigureCard(props: FigureCardProps) {
  const { testId, kind, ariaLabel, children } = props;

  return (
    <figure
      className="flex w-full flex-col gap-3 rounded-xl border border-border bg-card p-4"
      aria-label={ariaLabel}
      data-testid={testId}
      data-plot-kind={kind}
    >
      {children}
    </figure>
  );
}

interface BarRow {
  key: string;
  label: string;
  share: number;
  isCited: boolean;
}

interface BarRowsProps {
  testId: string;
  rows: Array<BarRow>;
  hasHighlight: boolean;
}

/** Share rows on `TrackBar`: colour carries the highlight, no extra prop. */
function BarRows(props: BarRowsProps) {
  const { testId, rows, hasHighlight } = props;

  return (
    <div className="flex flex-col gap-3">
      {rows.map((row, index) => {
        const isHighlighted = !hasHighlight || row.isCited;
        return (
          <div
            key={row.key}
            className="flex flex-col gap-1.5"
            data-testid={`${testId}-row-${index}`}
            data-cited={hasHighlight && row.isCited ? "true" : undefined}
          >
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span
                className={cn(
                  "min-w-0",
                  isHighlighted ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {row.label}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-foreground">
                {formatShare(row.share)}
              </span>
            </div>
            <TrackBar
              widthPct={row.share}
              color={isHighlighted ? HIGHLIGHT_COLOR : CONTEXT_COLOR}
              value={formatShare(row.share)}
              label={row.label}
              withTooltip={false}
            />
          </div>
        );
      })}
    </div>
  );
}

interface StorylineCutFigureProps {
  cuts: Array<FindingPlotCut>;
  findingIndex: number;
  headline: string;
}

/** A cut per chip, a `TrackBar` per group; counts-only groups get no bar. */
function StorylineCutFigure(props: StorylineCutFigureProps) {
  const { cuts, findingIndex, headline } = props;

  const [activeIndex, setActiveIndex] = useState(0);

  const { t } = useTranslation("app");

  const testId = `storyline-figure-${findingIndex}`;
  const cut = cuts[Math.min(activeIndex, cuts.length - 1)];
  const showChips =
    cuts.length >= 2 && cuts.every((candidate) => Boolean(candidate.chipLabel));
  const valueLine = cut.outcome ? cutValueLine(cut.outcome, t) : null;

  return (
    <FigureCard
      testId={testId}
      kind="cut"
      ariaLabel={t("studyReport.storyline.figure.byGroup", {
        title: cut.outcomeLabel ?? headline,
      })}
    >
      {showChips ? (
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label={t("studyReport.storyline.figure.chips")}
        >
          {cuts.map((candidate, index) => (
            <button
              key={candidate.requestId}
              type="button"
              className={index === activeIndex ? CHIP_PRESSED_CLASS : CHIP_IDLE_CLASS}
              aria-pressed={index === activeIndex}
              title={candidate.chipTitle ?? undefined}
              onClick={() => setActiveIndex(index)}
              data-testid={`storyline-figure-chip-${findingIndex}-${index}`}
            >
              {candidate.chipLabel}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        {valueLine ? (
          <Text
            size="xs"
            color="muted"
            className="min-w-0 flex-1 leading-snug"
            data-testid={`${testId}-value-line`}
          >
            {valueLine}
          </Text>
        ) : (
          <span className="flex-1" />
        )}
        <ReadStrengthTag
          readStrength={cut.readStrength}
          dataTestId={`${testId}-read`}
        />
      </div>
      <div className="flex flex-col gap-3" data-testid={`${testId}-cut-${cut.requestId}`}>
        {cut.groups.map((group, index) => {
          const widthPct = cutGroupWidthPct(cut, group);
          const countOnly =
            group.formatted === null && typeof group.count === "number"
              ? t("studyReport.storyline.cutCountOnly", {
                  count: group.count,
                  base: group.base,
                })
              : null;
          return (
            <div
              key={group.groupId}
              className="flex flex-col gap-1.5"
              data-testid={`${testId}-group-${index}`}
              data-thin={group.thin ? "true" : undefined}
              data-counts-only={widthPct === null ? "true" : undefined}
            >
              <div className="flex items-baseline justify-between gap-4 text-sm">
                <span className="min-w-0 text-foreground">
                  {group.label}
                  {/* A small group states its base inside its flag. */}
                  {group.thin ? (
                    <span
                      className="ml-2 text-xs tabular-nums text-muted-foreground"
                      data-testid={`${testId}-group-${index}-thin`}
                    >
                      {t("studyReport.storyline.cutSmallGroup", { count: group.base })}
                    </span>
                  ) : (
                    <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                      {t("studyReport.storyline.base", { count: group.base })}
                    </span>
                  )}
                </span>
                <span
                  className="shrink-0 font-medium tabular-nums text-foreground"
                  data-testid={`${testId}-group-${index}-value`}
                >
                  {group.formatted ?? countOnly}
                </span>
              </div>
              {widthPct !== null ? (
                <div
                  data-testid={`${testId}-group-${index}-bar`}
                  data-width-pct={String(widthPct)}
                >
                  <TrackBar
                    widthPct={widthPct}
                    color={HIGHLIGHT_COLOR}
                    value={group.formatted}
                    label={group.label}
                    withTooltip={false}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </FigureCard>
  );
}
